import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Paperclip, Trash2, UploadCloud, Download } from "lucide-react";
import { toast } from "sonner";

function prettyBytes(n) {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Task attachments. With `commentId` it scopes to a single comment's files
 * (and uploads tagged to that comment); without it, shows task/description-level
 * files only. `compact` renders a slimmer inline version for comment threads.
 */
export default function Attachments({ taskId, commentId = null, compact = false }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const load = async () => {
        if (!taskId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/tasks/${taskId}/attachments`);
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

    // Show only the files belonging to this scope (a comment, or the task itself).
    const visible = items.filter((a) =>
        commentId ? a.comment_id === commentId : !a.comment_id,
    );

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File too large (max 10MB)");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            if (commentId) form.append("comment_id", commentId);
            await api.post(`/tasks/${taskId}/attachments`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Uploaded");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDelete = async (a) => {
        try {
            await api.delete(`/attachments/${a.id}`);
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const openFile = async (a) => {
        try {
            const resp = await api.get(`/attachments/${a.id}/download`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(resp.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const fileInput = (
        <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={onFile}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.docx,.doc,.txt,.zip"
        />
    );

    // Compact variant used inside a comment.
    if (compact) {
        return (
            <div className="mt-2" data-testid={`comment-attachments-${commentId}`}>
                {visible.length > 0 && (
                    <ul className="mb-1 space-y-1">
                        {visible.map((a) => (
                            <li key={a.id} className="flex items-center gap-2 text-xs">
                                <Paperclip className="h-3 w-3 text-muted-foreground" />
                                <button
                                    type="button"
                                    className="truncate text-primary hover:underline"
                                    onClick={() => openFile(a)}
                                >
                                    {a.filename}
                                </button>
                                <span className="text-muted-foreground">
                                    ({prettyBytes(a.size)})
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:opacity-50"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`comment-attach-${commentId}`}
                >
                    {uploading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <Paperclip className="h-3 w-3" />
                    )}
                    Attach file
                </button>
                {fileInput}
            </div>
        );
    }

    return (
        <div className="mt-6 rounded-lg border border-border bg-card p-4" data-testid="attachments-section">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Attachments</span>
                    {visible.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                            ({visible.length})
                        </span>
                    )}
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid="attachments-upload"
                >
                    {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <>
                            <UploadCloud className="mr-1.5 h-4 w-4" /> Upload
                        </>
                    )}
                </Button>
                {fileInput}
            </div>

            {loading ? (
                <div className="grid place-items-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
            ) : visible.length === 0 ? (
                <p className="mt-3 text-xs text-muted-foreground">
                    No attachments yet. Upload supporting docs, invoices, or signed forms (max 10MB each).
                </p>
            ) : (
                <ul className="mt-3 divide-y divide-border">
                    {visible.map((a) => (
                        <li
                            key={a.id}
                            className="flex items-center justify-between gap-3 py-2"
                            data-testid={`attachment-row-${a.id}`}
                        >
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium">
                                    {a.filename}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {prettyBytes(a.size)} ·{" "}
                                    {new Date(a.created_at).toLocaleString("en-IN")}
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openFile(a)}
                                    data-testid={`attachment-open-${a.id}`}
                                >
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => onDelete(a)}
                                    data-testid={`attachment-delete-${a.id}`}
                                >
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
